/**
 * i18n Configuration
 *
 * Centralized locale configuration for the application.
 * Supported locales: US English (en-US) and Bangladeshi Bangla (bn-BD)
 */

export const i18nConfig = {
  defaultLocale: "en-US",
  locales: ["en-US", "bn-BD"],
} as const;

/**
 *
 */
export type Locale = (typeof i18nConfig.locales)[number];

export const localeNames: Record<Locale, string> = {
  "en-US": "English",
  "bn-BD": "বাংলা",
};

/**
 * Check if a locale is valid
 */
export function isValidLocale(locale: string): locale is Locale {
  return i18nConfig.locales.includes(locale as Locale);
}

/**
 * Pick the best supported locale from an `Accept-Language` header.
 *
 * Unlike `normalizeLocale`, this reports no match by falling through to the
 * default rather than forcing every tag to resolve — a header lists several
 * preferences in quality order, and the first one we actually support wins.
 */
export function resolveLocaleFromAcceptLanguage(
  header: string | null | undefined
): Locale {
  if (!header) {
    return i18nConfig.defaultLocale;
  }

  const preferences = header
    .split(",")
    .map((entry) => {
      const [tag, ...params] = entry.trim().split(";");
      const quality = params.find((param) => param.trim().startsWith("q="));

      return {
        tag: tag.trim().toLowerCase(),
        quality: quality ? Number.parseFloat(quality.trim().slice(2)) : 1,
      };
    })
    .filter(
      ({ tag, quality }) => tag && Number.isFinite(quality) && quality > 0
    )
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of preferences) {
    if (tag === "*") {
      break;
    }

    const language = tag.split("-")[0];

    const match = i18nConfig.locales.find((locale) => {
      const candidate = locale.toLowerCase();

      return candidate === tag || candidate.startsWith(`${language}-`);
    });

    if (match) {
      return match;
    }
  }

  return i18nConfig.defaultLocale;
}

/**
 * Normalize locale string - matches full locale or language prefix
 * e.g., "en" -> "en-US", "bn" -> "bn-BD", "en-GB" -> "en-US"
 */
export function normalizeLocale(locale: string): Locale {
  // Exact match
  if (isValidLocale(locale)) {
    return locale;
  }

  // Match by language prefix
  const langPrefix = locale.split("-")[0];
  const matched = i18nConfig.locales.find((l) => l.startsWith(langPrefix));

  return matched ?? i18nConfig.defaultLocale;
}
